<template>
  <div>
    <div class="relative flex justify-center items-center">
      <div class="absolute z-30 text-white px-6">
        <h1 class="text-4xl md:text-5xl leading-[50px] md:leading-[60px] font-serif text-center">{{
          $t('services.listening-desk.title') }}</h1>
      </div>
      <div class="absolute z-20 inset-0 bg-black opacity-40"></div>
      <nuxt-img sizes="xl:100vw lg:100vw md:100vw sm:100vw xs:100vw"
        class="object-cover min-h-[400px] w-full md:aspect-[3]" src="/images/services/listening-desk-hero.jpg"
        :alt="$t('services.listening-desk.title')" />
    </div>

    <div class="container mx-auto px-6 py-12">
      <p class="text-sm md:text-base text-gray-600 mb-12 max-w-4xl">
        {{ $t('services.listening-desk.description') }}
      </p>

      <div class="grid md:grid-cols-2 gap-12">
        <div class="bg-white rounded-lg p-8 shadow-lg">
          <h2 class="text-xl md:text-2xl font-serif mb-6">{{ $t('services.listening-desk.audience.title') }}</h2>
          <ul class="space-y-3 text-sm md:text-base">
            <li class="flex items-center gap-2">
              <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
              {{ $t('services.listening-desk.audience.students') }}
            </li>
            <li class="flex items-center gap-2">
              <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
              {{ $t('services.listening-desk.audience.parents') }}
            </li>
            <li class="flex items-center gap-2">
              <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
              {{ $t('services.listening-desk.audience.teachers') }}
            </li>
            <li class="flex items-center gap-2">
              <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
              {{ $t('services.listening-desk.audience.anyone') }}
            </li>
          </ul>
        </div>

        <div class="bg-white rounded-lg p-8 shadow-lg">
          <h2 class="text-xl md:text-2xl font-serif mb-6">{{ $t('services.listening-desk.offers.title') }}</h2>
          <ul class="space-y-3 text-sm md:text-base">
            <li class="flex items-center gap-2">
              <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
              {{ $t('services.listening-desk.offers.active-listening') }}
            </li>
            <li class="flex items-center gap-2">
              <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
              {{ $t('services.listening-desk.offers.emotional-support') }}
            </li>
            <li class="flex items-center gap-2">
              <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
              {{ $t('services.listening-desk.offers.guidance') }}
            </li>
            <li class="flex items-center gap-2">
              <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
              {{ $t('services.listening-desk.offers.safe-space') }}
            </li>
          </ul>
        </div>
      </div>

      <div class="mt-12 p-8 shadow-lg">
        <h2 class="text-xl md:text-2xl font-serif mb-6 ">{{ $t('services.listening-desk.how-it-works.title') }}</h2>
        <div class="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p class="text-sm md:text-base text-gray-600">
              {{ $t('services.listening-desk.how-it-works.description') }}
            </p>
            <ul class="mt-6 space-y-3 text-sm md:text-base">
              <li class="flex items-center gap-2">
                <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
                {{ $t('services.listening-desk.how-it-works.book') }}
              </li>
              <li class="flex items-center gap-2">
                <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
                {{ $t('services.listening-desk.how-it-works.safe-meeting') }}
              </li>
              <li class="flex items-center gap-2">
                <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
                {{ $t('services.listening-desk.how-it-works.share') }}
              </li>
              <li class="flex items-center gap-2">
                <Icon name="mdi:check-circle" class="text-blue-600 shrink-0" />
                {{ $t('services.listening-desk.how-it-works.receive') }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="bg-gray-100 p-8 md:p-12 rounded-2xl shadow-lg mx-auto mt-16">
        <div class="text-center mb-10">
          <h2 class="text-2xl md:text-3xl font-serif font-bold text-gray-900 mb-3">{{ $t('services.listening-desk.contact.title') }}</h2>
          <div class="w-20 h-1 bg-blue-600 mx-auto mb-4"></div>
          <p class="text-gray-600 max-w-2xl mx-auto">
            {{ $t('services.listening-desk.contact.description') }}
          </p>
        </div>
        <form @submit.prevent="submitForm" class="space-y-6 w-full max-w-2xl mx-auto scroll-mt-64" id="contact-form">
              <div class="space-y-2">
                <label for="name" class="block font-medium text-gray-700">{{ $t('services.listening-desk.form.name') }}</label>
                <input v-model="name" v-bind="nameAttrs" type="text" id="name" name="name" 
                  class="w-full h-12 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  :class="{ 'border-red-500': errors.name }" />
                <span v-if="errors.name" class="block text-sm text-red-600 mt-1">{{ errors.name }}</span>
              </div>

              <div>
                <label for="surname" class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.surname') }}</label>
                <input v-model="surname" v-bind="surnameAttrs" type="text" id="surname" name="surname"
                  class="form-input w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                  :class="{ 'input-error': errors.surname }" />
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.surname }}</span>
              </div>

              <div>
                <label class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.gender.label') }}</label>
                <div class="space-y-3">
                  <label v-for="g in genderOptions" :key="g.value" class="flex items-center gap-3 cursor-pointer group">
                    <div class="relative flex items-center justify-center w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-blue-600 transition-colors">
                      <input type="radio" :value="g.value" v-model="gender" v-bind="genderAttrs" name="gender" class="absolute opacity-0 w-0 h-0" />
                      <span class="absolute w-3 h-3 rounded-full bg-blue-600 opacity-0 transform scale-0 transition-all duration-200" :class="{ 'opacity-100 scale-100': gender === g.value }"></span>
                    </div>
                    <span class="text-gray-700 group-hover:text-blue-600 transition-colors">{{ $t(g.label) }}</span>
                  </label>
                </div>
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.gender }}</span>
              </div>

              <div>
                <label for="phone" class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.phone') }}</label>
                <input v-model="phone" v-bind="phoneAttrs" type="text" id="phone" name="phone" class="form-input w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                  :class="{ 'input-error': errors.phone }" />
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.phone }}</span>
              </div>

              <div>
                <label for="email" class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.email') }}</label>
                <input v-model="email" v-bind="emailAttrs" type="email" id="email" name="email"
                  class="form-input w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                  :class="{ 'input-error': errors.email }" />
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.email }}</span>
              </div>

              <div>
                <label class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.age.label') }}</label>
                <div class="space-y-3">
                  <label v-for="a in ageOptions" :key="a.value" class="flex items-center gap-3 cursor-pointer group">
                    <div class="relative flex items-center justify-center w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-blue-600 transition-colors">
                      <input type="radio" :value="a.value" v-model="age" v-bind="ageAttrs" name="age" class="absolute opacity-0 w-0 h-0" />
                      <span class="absolute w-3 h-3 rounded-full bg-blue-600 opacity-0 transform scale-0 transition-all duration-200" :class="{ 'opacity-100 scale-100': age === a.value }"></span>
                    </div>
                    <span class="text-gray-700 group-hover:text-blue-600 transition-colors">{{ $t(a.label) }}</span>
                  </label>
                </div>
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.age }}</span>
              </div>

              <div>
                <label class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.topics.label') }}</label>
                <div class="space-y-3">
                  <label v-for="t in topicOptions" :key="t.value" class="flex items-center gap-3 cursor-pointer group">
                    <div class="relative flex items-center justify-center w-5 h-5 border-2 border-gray-300 rounded group-hover:border-blue-600 transition-colors">
                      <input type="checkbox" :value="t.value" v-model="topics" v-bind="topicsAttrs" class="absolute opacity-0 w-0 h-0" />
                      <svg v-if="topics.includes(t.value)" class="w-3.5 h-3.5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <span class="text-gray-700 group-hover:text-blue-600 transition-colors">{{ $t(t.label) }}</span>
                  </label>
                  <input v-if="topics.includes('other')" v-model="topicsOther" type="text"
                    class="form-input mt-2 w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                    :placeholder="$t('services.listening-desk.form.topics.other-placeholder')" />
                </div>
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.topics }}</span>
              </div>

              <div>
                <label class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.zone.label') }}</label>
                <div class="relative">
                  <select v-model="zone" v-bind="zoneAttrs" 
                    class="appearance-none w-full h-14 pl-4 pr-10 py-2 text-base text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all shadow-sm">
                    <option value="" disabled selected class="text-gray-400">{{ $t('services.listening-desk.form.select-placeholder') }}</option>
                    <option v-for="z in zoneOptions" :key="z.value" :value="z.value" class="text-gray-700">{{ $t(z.label) }}</option>
                  </select>
                  <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.zone }}</span>
              </div>

              <div>
                <label class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.work-status.label') }}</label>
                <div class="space-y-3">
                  <label v-for="s in workStatusOptions" :key="s.value" class="flex items-center gap-3 cursor-pointer group">
                    <div class="relative flex items-center justify-center w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-blue-600 transition-colors">
                      <input type="radio" :value="s.value" v-model="workStatus" v-bind="workStatusAttrs" name="workStatus" class="absolute opacity-0 w-0 h-0" />
                      <span class="absolute w-3 h-3 rounded-full bg-blue-600 opacity-0 transform scale-0 transition-all duration-200" :class="{ 'opacity-100 scale-100': workStatus === s.value }"></span>
                    </div>
                    <span class="text-gray-700 group-hover:text-blue-600 transition-colors">{{ $t(s.label) }}</span>
                  </label>
                  <input v-if="workStatus === 'other'" v-model="workStatusOther" type="text"
                    class="form-input mt-2 w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                    :placeholder="$t('services.listening-desk.form.work-status.other-placeholder')" />
                </div>
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.workStatus }}</span>
              </div>

              <div>
                <label class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.time.label') }}</label>
                <div class="space-y-3">
                  <label v-for="t in timeOptions" :key="t.value" class="flex items-center gap-3 cursor-pointer group">
                    <div class="relative flex items-center justify-center w-5 h-5 border-2 border-gray-300 rounded group-hover:border-blue-600 transition-colors">
                      <input type="checkbox" :value="t.value" v-model="preferredTimes" v-bind="preferredTimesAttrs" class="absolute opacity-0 w-0 h-0" />
                      <svg v-if="preferredTimes.includes(t.value)" class="w-3.5 h-3.5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <span class="text-gray-700 group-hover:text-blue-600 transition-colors">{{ $t(t.label) }}</span>
                  </label>
                  <input v-if="preferredTimes.includes('other')" v-model="preferredTimesOther" type="text"
                    class="form-input mt-2 w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                    :placeholder="$t('services.listening-desk.form.time.other-placeholder')" />
                </div>
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.preferredTimes }}</span>
              </div>

              <div>
                <label class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.day.label') }}</label>
                <div class="space-y-3">
                  <label v-for="d in dayOptions" :key="d.value" class="flex items-center gap-3 cursor-pointer group">
                    <div class="relative flex items-center justify-center w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-blue-600 transition-colors">
                      <input type="radio" :value="d.value" v-model="preferredDay" v-bind="preferredDayAttrs" name="preferredDay" class="absolute opacity-0 w-0 h-0" />
                      <span class="absolute w-3 h-3 rounded-full bg-blue-600 opacity-0 transform scale-0 transition-all duration-200" :class="{ 'opacity-100 scale-100': preferredDay === d.value }"></span>
                    </div>
                    <span class="text-gray-700 group-hover:text-blue-600 transition-colors">{{ $t(d.label) }}</span>
                  </label>
                  <input v-if="preferredDay === 'other'" v-model="preferredDayOther" type="text"
                    class="form-input mt-2 w-full h-14 shadow-sm text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-lg border border-gray-200 focus:outline-none py-2 px-4"
                    :placeholder="$t('services.listening-desk.form.day.other-placeholder')" />
                </div>
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.preferredDay }}</span>
              </div>

              <div>
                <label class="block mb-2 font-serif text-base">{{ $t('services.listening-desk.form.message.label') }}</label>
                <textarea v-model="message" v-bind="messageAttrs" class="form-textarea w-full h-48 shadow-sm resize-none text-gray-600 placeholder:text-gray-400 text-base font-sans leading-7 rounded-2xl border border-gray-200 focus:outline-none px-4 py-4"
                  rows="5"></textarea>
                <span class="error-text mt-1 block text-sm text-red-600">{{ errors.message }}</span>
              </div>

              <div class="pt-2">
                <div class="flex items-start">
                  <label class="flex items-start gap-3 cursor-pointer group">
                    <div class="relative flex items-center justify-center w-5 h-5 border-2 border-gray-300 rounded group-hover:border-blue-600 transition-colors flex-shrink-0 mt-0.5">
                      <input type="checkbox" id="privacy" name="privacy" v-model="acceptedPrivacy" v-bind="acceptedPrivacyAttrs" class="absolute opacity-0 w-0 h-0" />
                      <svg v-if="acceptedPrivacy" class="w-3.5 h-3.5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <i18n-t for="checkbox" keypath="contacts.privacy-acceptance" tag="span" scope="global" class="text-sm text-gray-600 group-hover:text-blue-600 transition-colors">
                      <template v-slot:url>
                        <NuxtLink class="text-blue-600 hover:text-blue-700 font-medium underline transition-colors" :to="localePath('/privacy-policy')" target="_blank">
                          {{ $t('contacts.privacy-policy') }}
                        </NuxtLink>
                      </template>
                    </i18n-t>
                  </label>
                </div>
                <span v-if="errors.acceptedPrivacy" class="block text-sm text-red-600 mt-1">{{ errors.acceptedPrivacy }}</span>
              </div>

              <button 
                class="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-3 text-lg font-medium mt-8 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" 
                type="submit"
                :disabled="loading"
                :class="{ 'opacity-75 cursor-not-allowed': loading }"
              >
                <span v-if="!loading">{{ $t('services.listening-desk.form.submit') }}</span>
                <span v-else>{{ $t('services.listening-desk.form.sending') }}...</span>
              </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import * as yup from 'yup'
import { useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { useReCaptcha } from 'vue-recaptcha-v3'

const { t } = useI18n()
const loading = ref(false)
const recaptchaInstance = useReCaptcha()

// Validation schema
const schema = yup.object({
  name: yup.string().required(t('services.listening-desk.form.errors.required')),
  surname: yup.string().required(t('services.listening-desk.form.errors.required')),
  gender: yup.string()
    .oneOf(['female', 'male', 'other', 'prefer-not-to-say'], t('services.listening-desk.form.errors.required'))
    .required(t('services.listening-desk.form.errors.required')),
  phone: yup.string().required(t('services.listening-desk.form.errors.required')),
  email: yup.string().email(t('services.listening-desk.form.errors.email')).required(t('services.listening-desk.form.errors.required')),
  age: yup.string()
    .oneOf(['16-25', '26-40', '41-60', '61-75', 'over75'], t('services.listening-desk.form.errors.required'))
    .required(t('services.listening-desk.form.errors.required')),
  topics: yup.array()
    .min(1, t('services.listening-desk.form.errors.required'))
    .of(yup.string()),
  topicsOther: yup.string().when('topics', {
    is: (topics) => topics.includes('other'),
    then: (schema) => schema.required(t('services.listening-desk.form.errors.required')),
    otherwise: (schema) => schema.nullable()
  }),
  zone: yup.string()
    .oneOf(['center', 'is-murtas', 'flumini', 'sant-andrea', 'quartello', 'zona-musicisti', 'pitze-serra', 'selargius', 'quartucciu'], t('services.listening-desk.form.errors.required'))
    .required(t('services.listening-desk.form.errors.required')),
  workStatus: yup.string()
    .oneOf(['student', 'employed', 'unemployed', 'retired', 'other'], t('services.listening-desk.form.errors.required'))
    .required(t('services.listening-desk.form.errors.required')),
  workStatusOther: yup.string().when('workStatus', {
    is: 'other',
    then: (schema) => schema.required(t('services.listening-desk.form.errors.required')),
    otherwise: (schema) => schema.nullable()
  }),
  preferredTimes: yup.array()
    .min(1, t('services.listening-desk.form.errors.required'))
    .of(yup.string()),
  preferredTimesOther: yup.string().when('preferredTimes', {
    is: (preferredTimes) => preferredTimes.includes('other'),
    then: (schema) => schema.required(t('services.listening-desk.form.errors.required')),
    otherwise: (schema) => schema.nullable()
  }),
  preferredDay: yup.string()
    .oneOf(['monday', 'wednesday', 'friday', 'other'], t('services.listening-desk.form.errors.required'))
    .required(t('services.listening-desk.form.errors.required')),
  preferredDayOther: yup.string().when('preferredDay', {
    is: 'other',
    then: (schema) => schema.required(t('services.listening-desk.form.errors.required')),
    otherwise: (schema) => schema.nullable()
  }),
  message: yup.string().nullable(),
  acceptedPrivacy: yup.boolean().oneOf([true], t('services.listening-desk.form.errors.required')),
})

const genderOptions = [
  { value: 'female', label: 'services.listening-desk.form.gender.female' },
  { value: 'male', label: 'services.listening-desk.form.gender.male' },
  { value: 'other', label: 'services.listening-desk.form.gender.other' },
  { value: 'prefer-not-to-say', label: 'services.listening-desk.form.gender.prefer-not-to-say' }
]

const ageOptions = [
  { value: '16-25', label: 'services.listening-desk.form.age.16-25' },
  { value: '26-40', label: 'services.listening-desk.form.age.26-40' },
  { value: '41-60', label: 'services.listening-desk.form.age.41-60' },
  { value: '61-75', label: 'services.listening-desk.form.age.61-75' },
  { value: 'over75', label: 'services.listening-desk.form.age.over75' }
]

const topicOptions = [
  { value: 'self-esteem', label: 'services.listening-desk.form.topics.self-esteem' },
  { value: 'self-confidence', label: 'services.listening-desk.form.topics.self-confidence' },
  { value: 'career', label: 'services.listening-desk.form.topics.career' },
  { value: 'money', label: 'services.listening-desk.form.topics.money' },
  { value: 'anxiety', label: 'services.listening-desk.form.topics.anxiety' },
  { value: 'parenting', label: 'services.listening-desk.form.topics.parenting' },
  { value: 'isolation', label: 'services.listening-desk.form.topics.isolation' },
  { value: 'depression', label: 'services.listening-desk.form.topics.depression' },
  { value: 'conflicts', label: 'services.listening-desk.form.topics.conflicts' },
  { value: 'grief', label: 'services.listening-desk.form.topics.grief' },
  { value: 'emotions', label: 'services.listening-desk.form.topics.emotions' },
  { value: 'love', label: 'services.listening-desk.form.topics.love' },
  { value: 'other', label: 'services.listening-desk.form.topics.other' }
]

const zoneOptions = [
  { value: 'center', label: 'services.listening-desk.form.zone.center' },
  { value: 'is-murtas', label: 'services.listening-desk.form.zone.is-murtas' },
  { value: 'flumini', label: 'services.listening-desk.form.zone.flumini' },
  { value: 'sant-andrea', label: 'services.listening-desk.form.zone.sant-andrea' },
  { value: 'quartello', label: 'services.listening-desk.form.zone.quartello' },
  { value: 'zona-musicisti', label: 'services.listening-desk.form.zone.zona-musicisti' },
  { value: 'pitze-serra', label: 'services.listening-desk.form.zone.pitze-serra' },
  { value: 'selargius', label: 'services.listening-desk.form.zone.selargius' },
  { value: 'quartucciu', label: 'services.listening-desk.form.zone.quartucciu' }
]

const workStatusOptions = [
  { value: 'student', label: 'services.listening-desk.form.work-status.student' },
  { value: 'employed', label: 'services.listening-desk.form.work-status.employed' },
  { value: 'unemployed', label: 'services.listening-desk.form.work-status.unemployed' },
  { value: 'retired', label: 'services.listening-desk.form.work-status.retired' },
  { value: 'other', label: 'services.listening-desk.form.work-status.other' }
]

const timeOptions = [
  { value: '10-13', label: 'services.listening-desk.form.time.10-13' },
  { value: '17-19', label: 'services.listening-desk.form.time.17-19' },
  { value: 'other', label: 'services.listening-desk.form.time.other' }
]

const dayOptions = [
  { value: 'monday', label: 'services.listening-desk.form.day.monday' },
  { value: 'wednesday', label: 'services.listening-desk.form.day.wednesday' },
  { value: 'friday', label: 'services.listening-desk.form.day.friday' },
  { value: 'other', label: 'services.listening-desk.form.day.other' }
]

// Initialize useForm with schema
const { handleSubmit, defineField, errors, resetForm, meta, setFieldValue, setFieldTouched } = useForm({
  validationSchema: schema,
  initialValues: {
    name: '',
    surname: '',
    gender: '',
    phone: '',
    email: '',
    age: '',
    topics: [],
    topicsOther: '',
    zone: '',
    workStatus: '',
    workStatusOther: '',
    preferredTimes: [],
    preferredTimesOther: '',
    preferredDay: '',
    preferredDayOther: '',
    message: '',
    acceptedPrivacy: false,
  }
})

// Define form fields
const [name, nameAttrs] = defineField('name')
const [surname, surnameAttrs] = defineField('surname')
const [gender, genderAttrs] = defineField('gender')
const [phone, phoneAttrs] = defineField('phone')
const [email, emailAttrs] = defineField('email')
const [age, ageAttrs] = defineField('age')
const [topics, topicsAttrs] = defineField('topics')
const [topicsOther, topicsOtherAttrs] = defineField('topicsOther')
const [zone, zoneAttrs] = defineField('zone')
const [workStatus, workStatusAttrs] = defineField('workStatus')
const [workStatusOther, workStatusOtherAttrs] = defineField('workStatusOther')
const [preferredTimes, preferredTimesAttrs] = defineField('preferredTimes')
const [preferredTimesOther, preferredTimesOtherAttrs] = defineField('preferredTimesOther')
const [preferredDay, preferredDayAttrs] = defineField('preferredDay')
const [preferredDayOther, preferredDayOtherAttrs] = defineField('preferredDayOther')
const [message, messageAttrs] = defineField('message')
const [acceptedPrivacy, acceptedPrivacyAttrs] = defineField('acceptedPrivacy');

// Watch for changes to clear 'other' fields
watch(gender, (val) => {
  if (val !== 'other') setFieldValue('genderOther', '')
})
watch(workStatus, (val) => {
  if (val !== 'other') setFieldValue('workStatusOther', '')
})
watch(preferredTimes, (val) => {
  if (!val.includes('other')) setFieldValue('preferredTimesOther', '')
})
watch(preferredDay, (val) => {
  if (val !== 'other') setFieldValue('preferredDayOther', '')
})
watch(topics, (val) => {
  if (!val.includes('other')) setFieldValue('topicsOther', '')
})

// reCAPTCHA logic
const recaptcha = async () => {
  await recaptchaInstance?.recaptchaLoaded()
  const token = await recaptchaInstance?.executeRecaptcha('submitForm')
  return token
}

// Form submission handler
const submitForm = handleSubmit(async (values, actions) => {
  loading.value = true
  try {
    const recaptchaToken = await recaptcha()
    values.recaptchaToken = recaptchaToken

    const response = await $fetch('/api/listening-desk', {
      method: 'POST',
      body: values,
    })

    loading.value = false

    if (response.message === 'Success') {
      alert(t('services.listening-desk.form.success'))
      actions.resetForm()
    } else {
      alert(t('services.listening-desk.form.error'))
    }
  } catch (error) {
    loading.value = false
    console.error('Submission error:', error)
    alert(t('services.listening-desk.form.error'))
  }
})
</script>

<style scoped>
.error-text {
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>